import { computed, type Ref } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { ref } from 'vue'

const SCALE_FACTOR = 0.025
const FULLSCREEN_SCALE_FACTOR = 0.03
const MIN_FONT_SIZE_PX = 14
const MAX_FONT_SIZE_PX = 64
// Hard ceiling once the user multiplier is applied — keeps the largest preset
// readable in fullscreen without letting it grow absurdly on ultra-wide players.
const USER_MAX_FONT_SIZE_PX = 120

export interface UseSubtitleResizeOptions {
  containerRef: Ref<HTMLElement | null>
  isFullscreen: Ref<boolean>
  /** Viewer-chosen size multiplier (1 = historical auto size). */
  userScale?: Ref<number>
}

export function useSubtitleResize(options: UseSubtitleResizeOptions) {
  const { containerRef, isFullscreen, userScale } = options
  const containerWidth = ref(0)

  useResizeObserver(containerRef, (entries) => {
    const entry = entries[0]
    if (entry) {
      containerWidth.value = entry.contentRect.width
    }
  })

  const subtitleFontSizeStyle = computed<Record<string, string>>(() => {
    if (containerWidth.value === 0) return {}

    const scale = isFullscreen.value ? FULLSCREEN_SCALE_FACTOR : SCALE_FACTOR
    const rawSize = containerWidth.value * scale
    const autoSize = Math.max(MIN_FONT_SIZE_PX, Math.min(MAX_FONT_SIZE_PX, rawSize))

    const multiplier = userScale?.value ?? 1
    const finalSize = Math.max(MIN_FONT_SIZE_PX, Math.min(USER_MAX_FONT_SIZE_PX, autoSize * multiplier))

    return {
      '--vp-subtitle-font-size-base': `${finalSize}px`,
      // Lift the clamp() ceiling so the chosen size isn't capped below itself.
      '--vp-subtitle-font-size-max': `${Math.max(MAX_FONT_SIZE_PX, finalSize)}px`,
    }
  })

  return {
    subtitleFontSizeStyle,
  }
}
